///client/src/pages/Upgrade.js
///client/src/pages/Upgrade.js
import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import { handleDeleteAccount } from '../utils/accountDeletion';
import CancelSubscriptionModal from '../components/modals/CancelSubscriptionModal';



function Upgrade() {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(14);
  const [isAnnual, setIsAnnual] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Extract fetchAccount function so it can be called from multiple places
// Update the fetchAccount function in Upgrade.js
const fetchAccount = async () => {
    try {
      if (!user) {
        setError('You must be logged in to view this page');
        setLoading(false);
        return;
      }
      
      // First fetch the user to get the account_id
      console.log('Fetching user data for clerk ID:', user.id);
      
      // Step 1: Get user to find account_id
      const userResponse = await fetch(`/api/users/by-clerk/${user.id}`);
      
      if (!userResponse.ok) {
        console.error('Failed to fetch user:', userResponse.status);
        setError('Unable to fetch user details. Please try again later.');
        setLoading(false);
        return;
      }
      
      const userData = await userResponse.json();
      console.log('User data retrieved:', userData);
      
      if (!userData || !userData.account_id) {
        setError('Your account is not properly set up. Please contact support.');
        setLoading(false);
        return;
      }
      
      // Store the account_id for upgrade process
      const userAccountId = userData.account_id;
      setAccountId(userAccountId);
      
      // Step 2: Fetch account using account_id
      console.log('Fetching account with ID:', userAccountId);
      const accountResponse = await fetch(`/api/account/${userAccountId}`);
      
      if (!accountResponse.ok) {
        setError('Failed to fetch account details. Please try again later.');
        setLoading(false);
        return;
      }
      
      let accountData = await accountResponse.json();
      console.log('Account data:', accountData);
      
      // Auto-fix any accounts that are stuck in trialing status
      if (accountData.plan !== 'free_trial' && accountData.subscriptionStatus === 'trialing') {
        console.log('Found paid account with trialing status, attempting to fix...');
        try {
          const fixResponse = await fetch(`/api/debug/fix-trialing/${userAccountId}`, {
            method: 'POST'
          });
          if (fixResponse.ok) {
            const fixResult = await fixResponse.json();
            if (fixResult.success) {
              console.log('Fixed trialing status');
              accountData = fixResult.account;
            }
          }
        } catch (fixError) {
          console.error('Error fixing trialing status:', fixError);
        }
      }

      if (accountData.plan === 'free_basic' && accountData.subscriptionStatus !== 'free') {
        console.log('Found free_basic account with incorrect status, attempting to fix...');
        try {
          const fixResponse = await fetch(`/api/debug/fix-trialing/${userAccountId}`, {
            method: 'POST'
          });
          if (fixResponse.ok) {
            const fixResult = await fixResponse.json();
            if (fixResult.success) {
              console.log('Fixed free_basic account status to free');
              accountData = fixResult.account;
            }
          }
        } catch (fixError) {
          console.error('Error fixing free_basic account status:', fixError);
        }
      }
      else if ((accountData.plan.includes('plus') || accountData.plan.includes('advanced')) && 
               (accountData.subscriptionStatus === 'trialing' || accountData.subscriptionStatus === 'free')) {
        console.log('Found paid account with incorrect status, attempting to fix...');
        try {
          const fixResponse = await fetch(`/api/debug/fix-trialing/${userAccountId}`, {
            method: 'POST'
          });
          if (fixResponse.ok) {
            const fixResult = await fixResponse.json();
            if (fixResult.success) {
              console.log('Fixed paid account status to active');
              accountData = fixResult.account;
            }
          }
        } catch (fixError) {
          console.error('Error fixing paid account status:', fixError);
        }
      }
      
      setAccount(accountData);
      
      // Calculate days remaining from subscription end date
      if (accountData?.subscription?.currentPeriodEnd) {
        const endDate = new Date(accountData.subscription.currentPeriodEnd);
        const currentDate = new Date();
        const timeDiff = endDate - currentDate;
        const daysRemain = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
        setDaysRemaining(Math.max(0, daysRemain));
      }
      // Calculate days remaining if on free plan
      else if (accountData.plan === 'free_trial') {
        const creationDate = new Date(accountData.createdAt);
        const currentDate = new Date();
        const timeDiff = (14 * 24 * 60 * 60 * 1000) - (currentDate - creationDate);
        const daysRemainCalc = Math.max(0, Math.ceil(timeDiff / (24 * 60 * 60 * 1000)));
        setDaysRemaining(daysRemainCalc);
        console.log('Days remaining on free plan:', daysRemainCalc);
      }
      
      // Check for URL parameters that might contain messages
      const params = new URLSearchParams(window.location.search);
      const errorMessage = params.get('error');
      const successMsg = params.get('success');
      
      if (errorMessage) {
        setError(errorMessage);
        // Clean up the URL
        window.history.replaceState({}, document.title, '/upgrade');
      }
      
      if (successMsg) {
        setSuccessMessage(successMsg);
        // Clean up the URL
        window.history.replaceState({}, document.title, '/upgrade');
      }
    } catch (err) {
      console.error('Error fetching account:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on component mount
  useEffect(() => {
    setProcessingPayment(false);
    fetchAccount();
  }, [user]);

  // Updated to use accountId and include billing cycle preference
  const handleSubscribe = async (plan) => {
    try {
      setProcessingPayment(true);
      
      // Make sure we have the account ID
      if (!accountId) {
        console.error('No account ID available');
        setError('Your account is not properly set up. Please contact support.');
        setProcessingPayment(false);
        return;
      }
      
      // Add billing cycle to plan name if not already included
      let fullPlanName = plan;
      if (!plan.includes('_')) {
        fullPlanName = `${plan}_${isAnnual ? 'annual' : 'monthly'}`;
      }
      
      // Create descriptive information for Stripe
      let planDescription = '';
      if (fullPlanName.includes('free')) {
        planDescription = 'Basic Plan - Up to 3 QR codes and 10 trackable links';
      } else if (fullPlanName.includes('plus')) {
        planDescription = 'Plus Plan - Up to 100 QR codes, 500 trackable links and advanced analytics';
      } else if (fullPlanName.includes('advanced')) {
        planDescription = 'Advanced Plan - Up to 500 QR codes, 1000 trackable links and advanced analytics';
      } else if (fullPlanName.includes('business')) {
        planDescription = 'Business Plan - Unlimited QR codes, links, enterprise analytics and team management';
      }
      
      console.log(`Subscribing account ${accountId} to plan: ${fullPlanName}`);
      
      const response = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          accountId, 
          plan: fullPlanName,
          planDescription
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }
      
      const { url } = await response.json();
      window.location.href = url; // Redirect to Stripe checkout
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError(err.message);
      setProcessingPayment(false);
    }
  };
  
  // Calculate prices based on billing frequency
  const getPriceDisplay = (plan) => {
    if (isAnnual) {
      switch(plan) {
        case 'basic':
          return { 
            price: 'Free', 
            period: '', 
            fullPrice: 'Free', 
            discount: '',
            QR: '3',
            links: '10',
            subtitle: "Start your journey"
          };
        case 'plus':
          return { 
            price: '$8.33', 
            period: '/mo', 
            fullPrice: '$100.00/year + tax', 
            discount: 'Save 16%',
            QR: '100',
            links: '500',
            subtitle: "Growing needs"
          };
        case 'advanced':
          return { 
            price: '$40.00', 
            period: '/mo', 
            fullPrice: '$480.00/year + tax', 
            discount: 'Save 11%',
            QR: '500',
            links: '1000',
            subtitle: "Most popular for professionals" 
          };
        case 'business':
          return { 
            price: 'Custom', 
            period: '', 
            fullPrice: '', 
            discount: '',
            subtitle: "Tailored enterprise solutions"
          };
        default:
          return { 
            price: 'Custom', 
            period: '', 
            fullPrice: '', 
            discount: '',
            subtitle: "Custom pricing to fit your needs"
          };
      }
    } else {
      switch(plan) {
        case 'basic':
          return { 
            price: 'Free', 
            period: '', 
            fullPrice: '', 
            discount: '',
            QR: '3',
            links: '10',
            subtitle: "Start your journey" 
          };
        case 'plus':
          return { 
            price: '$10', 
            period: '/mo', 
            fullPrice: '', 
            discount: '',
            QR: '100',
            links: '500',
            subtitle: "Growing needs" 
          };
        case 'advanced':
          return { 
            price: '$45', 
            period: '/mo', 
            fullPrice: '', 
            discount: '',
            QR: '500',
            links: '1000',
            subtitle: "Most popular for professionals" 
          };
        case 'business':
          return { 
            price: 'Custom', 
            period: '', 
            fullPrice: '', 
            discount: '',
            subtitle: "Tailored enterprise solutions" 
          };
        default:
          return { 
            price: 'Custom', 
            period: '', 
            fullPrice: '', 
            discount: '',
            subtitle: "Custom pricing to fit your needs" 
          };
      }
    }
  };

  const getPlanDetails = () => {
    switch (account?.plan) {
      case 'free_trial':
        return {
          name: 'Basic',
          limits: '3 QR codes and 10 shortened links (active for 14 days)',
          cta: 'Upgrade Now'
        };
      case 'basic_monthly':
        return {
          name: 'Basic Monthly',
          description: 'Full access to Basic features (monthly billing)',
          limits: '3 QR codes and 10 trackable links',
          cta: 'Subscribe Now'
        };
        case 'basic_plus':
          return {
            name: 'Basic+',
            description: 'Full access to Basic features',
            limits: '3 QR codes and 10 trackable + advanced analytics',
            cta: 'Subscribe Now'
          };
        case 'free_basic':
          return {
            name: 'Free Basic Plan',
            description: 'Full access to Basic features',
            limits: '3 QR codes and 10 trackable links',
            cta: 'Subscribe Now'
          };
      case 'basic_annual':
        return {
          name: 'Basic Annual',
          description: 'Full access to Basic features (annual billing)',
          limits: '3 QR codes and 10 trackable links',
          cta: 'Subscribe Now'
        };
        case 'plus_monthly':
          return {
            name: 'Plus Monthly',
            description: 'Full access to Plus features (monthly billing)',
            limits: '100 QR codes and 500 trackable links',
            cta: 'Subscribe Now'
          };
        case 'plus_annual':
          return {
            name: 'Plus Annual',
            description: 'Full access to Plus features (annual billing)',
            limits: '100 QR codes and 500 trackable links',
            cta: 'Subscribe Now'
          };
      case 'advanced_annual':
        return {
          name: 'Advanced Annual',
          description: 'Full access to Advanced features (annual billing)',
          limits: '500 QR codes and 1000 trackable links',
          cta: 'Subscribe Now'
        };
      case 'advanced_monthly':
        return {
          name: 'Advanced Monthly',
          description: 'Full access to Advanced features (monthly billing)',
          limits: '500 QR codes and 1000 trackable links',
          cta: 'Subscribe Now'
        };
      default:
        return {
          name: 'Unknown Plan',
          description: 'Please contact support',
          limits: 'Unknown limits',
          cta: 'Contact Support'
        };
    }
  };


  // Delete account confirmation modal


  const currentPlan = getPlanDetails();
  const basicPrice = getPriceDisplay('basic');
  const plusPrice = getPriceDisplay('plus');
  const advancedPrice = getPriceDisplay('advanced');
  const businessPrice = getPriceDisplay('business');

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-6 max-w-4xl mx-auto mt-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
            <p className="text-sm text-red-700 mt-1">
              <button onClick={() => window.location.reload()} className="underline">
                Try refreshing the page
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Subscription Plans</h1>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border-l-4 border-green-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}
      

      {/* Current Plan Card */}
      <div className="max-w-4xl mx-auto">
      <div className="mb-8 bg-blue-50 p-6 rounded-lg border border-blue-200">
        <h2 className="text-xl font-semibold mb-2">Your Current Plan: {currentPlan.name}</h2>
        <p className="text-gray-600 mb-2">{currentPlan.description}</p>
        <p className="text-gray-600 mb-4">Limits: {currentPlan.limits}</p>
        
        {account?.plan_status === 'inactive' && (
          <div className="bg-yellow-100 text-yellow-800 p-3 rounded-md mb-4">
            <p>Your plan is currently inactive. Upgrade to restore access to all features.</p>
          </div>
        )}
        
        {/* Show days remaining if on free plan */}
        {/* {account?.plan === 'free_trial' && daysRemaining >= 1 && (
          <div className="bg-blue-100 text-blue-800 p-3 rounded-md mb-4">
            <p>Your free QR codes and links will expire in {daysRemaining} days. Upgrade to keep them active.</p>
          </div>
        )}

      {(account.plan === 'free_trial' && daysRemaining <= 0) && (
          <div className="bg-red-100 font-medium text-red-800 p-3 rounded-md mb-4">
            <p>Your free trial has expired. Upgrade now to reactivate your links and QR codes.</p>
          </div>
        )} */}
        
        {/* Subscription cancelled message - ONLY show this if subscription status is downgraded */}
        {account?.subscriptionStatus === 'downgraded' && (
          <div className="bg-yellow-100 text-yellow-800 p-3 rounded-md mb-4">
            <p>Your subscription has been cancelled. You'll have access to premium features until the end of your billing period. To reactivate choose a plan below.</p>
          </div>
        )}
        
        {/* Basic plan actions - allow upgrade to Advanced, and only show Cancel if not already cancelled */}
        {
        ((account?.plan === 'free_basic' || account && account.plan === 'basic_plus') ) && (
  <div className="space-y-2">
    <p className="text-m font-semibold text-blue-700">
      Need more? Upgrade to our Plus Plan and get 100 QR codes and 500 trackable links. 
    </p>
    <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => handleSubscribe('plus')}
                disabled={processingPayment}
                className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded font-semibold transition duration-200"
              >
                {processingPayment ? 'Processing...' : 'Upgrade to Plus ($10/mo)'}
              </button>
              
              {/* Only show Cancel button if subscription is not already cancelled */}
              {account?.subscriptionStatus !== 'downgraded' && account?.plan !== 'free_basic' && (
                <button 
                  onClick={() => setShowCancelModal(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold transition duration-200"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        )}
        {
        ((account?.plan === account?.plan === 'plus_annual' || account?.plan === 'plus_monthly')) && (
  <div className="space-y-2">
    <p className="text-m font-semibold text-blue-700">
      Need more? Upgrade to our Advanced plan and get 500 QR codes and 1000 trackable links. 
    </p>
    <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => handleSubscribe('advanced')}
                disabled={processingPayment}
                className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded font-semibold transition duration-200"
              >
                {processingPayment ? 'Processing...' : isAnnual ? 'Upgrade to Advanced (480/year)' : 'Upgrade to Advanced ($45/mo)'}
              </button>
              
              {/* Only show Cancel button if subscription is not already cancelled */}
              {account?.subscriptionStatus !== 'downgraded' && (
                <button 
                  onClick={() => setShowCancelModal(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold transition duration-200"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        )}

        {/* Advanced plan actions - only show Cancel if not already cancelled */}
        {(account?.plan === 'advanced_monthly' || account?.plan === 'advanced_annual') &&  (
          <button 
            onClick={() => setShowCancelModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium transition duration-200"
          >
            Cancel Subscription
          </button>
        )}

        

                {/* Advanced plan actions - only show Cancel if not already cancelled */}
                {/* {(account?.plan === 'plus_monthly' || account?.plan === 'plus_annual') && account?.subscriptionStatus !== 'downgraded' && (
          <button 
            onClick={() => setShowCancelModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium transition duration-200"
          >
            Cancel Subscription
          </button>
        )} */}
        
        {/* Delete account button - shown for all plans */}
        {/* <div className="mt-6 pt-6 border-t border-gray-200">
          <button 
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Delete Account
          </button>
        </div> */}
      </div>
      </div>
      
      {/* Plan Comparison - Centered with toggle */}
      {(account?.plan === 'free_basic' || account && account.plan === 'basic_plus' || account?.subscriptionStatus === 'downgraded') && (
        <>
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold mb-4">Choose the perfect plan for your needs</h2>
            <p className="text-gray-600 mb-6">All plans include core tracking features.</p>
            
            {/* Billing Toggle */}
            <div className="flex justify-center items-center space-x-3 mb-8">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-blue-600' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button 
                className="relative inline-flex h-6 w-11 items-center rounded-full"
                onClick={() => setIsAnnual(!isAnnual)}
              >
                <span className={`inline-block h-6 w-11 rounded-full transition ${isAnnual ? 'bg-blue-600' : 'bg-gray-300'}`}></span>
                <span 
                  className={`absolute inline-block h-4 w-4 transform rounded-full bg-white transition ${isAnnual ? 'translate-x-6' : 'translate-x-1'}`}
                >
                </span>
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-blue-600' : 'text-gray-500'}`}>
                Annual <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded ml-1">Save up to 15%</span>
              </span>
            </div>
          </div>
          
          <div className="grid max-w-5xl w-full mx-auto grid-cols-1 md:grid-cols-3 gap-6">
          {/* Basic Plan */}
            {/* <div className="border bg-white rounded-lg overflow-hidden shadow-sm">
              <div className="p-6"> */}
                {/* <h3 className="text-xl font-bold mb-2">Basic</h3>
                <p className="text-gray-600 mb-4">Personal Use</p>
                <p className="text-3xl font-bold mb-2">{basicPrice.price}<span className="text-lg text-gray-600">{basicPrice.period}</span></p>
                
                {/* Subtitle that uses the better copy */}
                {/* <p className="text-gray-600 mb-2">{basicPrice.subtitle}</p>
                
                {isAnnual && (
                  <div className="mb-2">
                    <p className="text-sm text-gray-600">{basicPrice.fullPrice}</p>
                    <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded mt-1">{basicPrice.discount}</span>
                  </div>
                )}
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>3 QR codes</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>10 trackable links</span>
                  </li> */}
                  {/* <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Unlimited scans and clicks</span>
                  </li> */}
                  {/* <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Basic analytics</span>
                  </li> */}
                  {/* <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Full customization</span>
                  </li>
                </ul> */}
                
                {/* Show appropriate CTA based on current plan */}
                {/* {account?.plan === 'free_trial' ? (
                  <button 
                    onClick={() => handleSubscribe('basic')}
                    disabled={processingPayment}
                    className="w-full bg-gray-300 hover:bg-gray-500 text-gray-900 px-4 py-2 rounded font-medium transition duration-200"
                  >
                    Upgrade Current Plan
                  </button>
                ) : (account?.plan === 'advanced_annual' && account?.subscriptionStatus === 'downgraded') ? (
                  <button 
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded font-medium"
                    disabled
                  >
                    Restart Plan
                  </button>
                ) : 
                 (account?.plan === 'basic_annual' && account?.subscriptionStatus === 'downgraded') ? (
                    <button 
                      className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded font-medium"
                      disabled
                    >
                      Restart Plan
                    </button>
                  ) : (
                  <button 
                    onClick={() => handleSubscribe('basic')}
                    disabled={processingPayment}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition duration-200"
                  >
                    {processingPayment ? 'Processing...' : 'Select Plan'}
                  </button>
                )} */}
              {/* </div>
            </div> */} 

                      {/* Basic + Plan */}
                      <div className="border rounded-lg bg-white overflow-hidden shadow-sm relative">

              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Plus</h3>
                <p className="text-gray-600 mb-4">Small Businesses</p>
                <p className="text-3xl font-bold mb-2">{plusPrice.price}<span className="text-lg text-gray-600">{plusPrice.period}</span></p>
                
                {/* Subtitle that uses the better copy */}
                <p className="text-gray-600 mb-2">{plusPrice.subtitle}</p>
                
                {isAnnual && (
                  <div className="mb-2">
                    <p className="text-m font-semibold text-gray-600">{plusPrice.fullPrice}                     <span className="inline-block ml-3 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded mt-1">{plusPrice.discount}</span>
                    </p>
                  </div>
                )}
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>100 QR codes</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>500 trackable links</span>
                  </li>
                  {/* <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Unlimited scans and clicks</span>
                  </li> */}
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Advanced analytics</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Full customization</span>
                  </li>
                </ul>
                
                {/* Show appropriate CTA based on current plan */}
                {(account?.plan === 'plus_monthly' || account?.plan === 'plus_annual') ? (
                  <button 
                    onClick={() => handleSubscribe('plus')}
                    className="w-full bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded font-semibold transition duration-200"
                    disabled={processingPayment}
                  >
                    {processingPayment ? 'Processing...' : (account?.plan && account.plan.startsWith('plus') ? 'Upgrade Now' : 'Select Plan')}
                  </button>
                ) : (
                  <button 
                    onClick={() => handleSubscribe('plus')}
                    disabled={processingPayment}
                    className="w-full bg-white border border-gray-900 hover:bg-gray-100 text-gray-900 px-4 py-2 rounded font-medium transition duration-200"
                    >
                    {processingPayment ? 'Processing...' : (account?.plan && account.plan.startsWith('plus') ? 'Upgrade Now' : 'Upgrade to Plus')}
                  </button>
                )}
              </div>
            </div>
            
            {/* Advanced Plan */}
            <div className="border bg-blue-50 rounded-lg overflow-hidden shadow-sm relative">
              <div className="bg-blue-500 text-white py-2 text-center">
                <span className="font-semibold">Most Popular</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl text-blue-700 font-bold mb-2">Advanced</h3>
                <p className="text-blue-700  mb-4">For Professionals</p>
                <p className="text-3xl text-blue-700 font-bold mb-2">{advancedPrice.price}<span className="text-lg text-blue-700 ">{advancedPrice.period}</span></p>
                
                {/* Subtitle that uses the better copy */}
                <p className="text-blue-700  mb-2">{advancedPrice.subtitle}</p>
                
                {isAnnual && (
                  <div className="mb-2">
                    <p className="text-m font-semibold text-blue-700 ">{advancedPrice.fullPrice}                    <span className="inline-block bg-white text-green-600 text-xs ml-3 px-2 py-0.5 rounded mt-1">{advancedPrice.discount}</span>
                    </p>
                  </div>
                )}
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-m font-semibold text-blue-700 ">500 QR codes</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-m font-semibold text-blue-700 ">1000 trackable links</span>
                  </li>
                  {/* <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Unlimited scans and clicks</span>
                  </li> */}
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-m font-semibold text-blue-700 ">Advanced analytics</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-m font-semibold text-blue-700 ">Full customization</span>
                  </li>
                </ul>
                
                {/* Show appropriate CTA based on current plan */}
                {(account?.plan === 'advanced_monthly' || account?.plan === 'advanced_annual') ? (
                  <button 
                    onClick={() => handleSubscribe('advanced')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
                    disabled={processingPayment}
                  >
                    {processingPayment ? 'Processing...' : (account?.plan && account.plan.startsWith('basic') ? 'Upgrade Now' : 'Upgrade to Advanced')}
                  </button>
                ) : (
                  <button 
                    onClick={() => handleSubscribe('advanced')}
                    disabled={processingPayment}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold transition duration-200"
                  >
                    {processingPayment ? 'Processing...' : (account?.plan && account.plan.startsWith('basic') ? 'Upgrade Now' : 'Upgrade to Advanced')}
                  </button>
                )}
              </div>
            </div>
            
            {/* Business Plan */}
            <div className="border bg-white rounded-lg overflow-hidden shadow-sm">
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Business</h3>
                <p className="text-gray-600 mb-4">For teams</p>
                <p className="text-3xl font-bold mb-2">{businessPrice.price}</p>
                
                {/* Subtitle that uses the better copy */}
                <p className="text-gray-600 mb-6">{businessPrice.subtitle}</p>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Fits your QR code & link needs</span>
                  </li>
                  {/* <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Redemtion code access</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>User verification - no need to print thousands of unique QR codes</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Enterprise analytics</span>
                  </li> */}
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Team management</span>
                  </li>
                  {/* <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>API access</span>
                  </li> */}
                </ul>
                
                <button 
  onClick={() => window.location.href = 'mailto:sales@boxyqr.com?subject=BoxyQR Sales Inquiry&body=Hello BoxyQR Sales Team,%0A%0AI\'m interested in learning more about your services.'}
  className="w-full bg-white border border-gray-900 hover:bg-gray-100 text-gray-900 px-4 py-2 rounded font-medium transition duration-200"
>
  Contact Sales
</button>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto">
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">What happens to my QR codes after the free trial?</h3>
            <p className="text-gray-600">
              On a free Basic trial plan, your QR codes and shortened links will remain active for 14 days. 
              After upgrading to a paid plan, all your QR codes and links will remain active as long as your subscription is active.
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-2">Can I cancel my subscription anytime?</h3>
            <p className="text-gray-600">
              Yes, you can cancel your subscription at any time. After cancellation, you'll continue to have access to your paid features until the end of your billing period.
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-2">How do I upgrade my plan?</h3>
            <p className="text-gray-600">
              Simply select the plan that best fits your needs on this page and follow the checkout process. If you're starting with a 14-day free trial, you'll need to enter your payment information, but you won't be charged until the trial period ends.
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-2">What's the difference between monthly and annual billing?</h3>
            <p className="text-gray-600">
              Annual billing offers a significant discount compared to monthly billing. You pay for a full year upfront and save compared to paying month-by-month. Your subscription will automatically renew at the end of the annual period unless canceled.
            </p>
          </div>

          <div>
  <h3 className="text-lg font-semibold mb-2">How do I get in touch with someone?</h3>
  <p className="text-gray-600">
    Email us with any questions at <a 
      href="mailto:support@boxyqr.com?subject=BoxyQR Account Help&" 
      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
    >
      support@boxyqr.com
    </a>
  </p>
</div>
          
          <div>
            <h3 className="text-lg font-semibold mb-2">How do I delete my account?</h3>
            <p className="text-gray-600">
              You can delete your account from the management account page. Please note that deleting your account will permanently remove all your data, including QR codes and tracking information, and cancel any active subscriptions.
            </p>
          </div>
        </div>
      </div>
      </div>
      
      {/* Delete Account Confirmation Modal */}
      <DeleteConfirmModal
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        accountId={accountId}
        user={user}
        setError={setError}
        processingPayment={processingPayment}
        setProcessingPayment={setProcessingPayment}
      />
            
      <CancelSubscriptionModal
        showCancelModal={showCancelModal}
        setShowCancelModal={setShowCancelModal}
        account={account}
        accountId={accountId}
        processingPayment={processingPayment}
        setProcessingPayment={setProcessingPayment}
        setError={setError}
        onCanceled={() => {
          // Refresh account data after cancellation
          setProcessingPayment(false);
          // Refresh account data
          fetchAccount();
        }}
      />
    </div>
  );
}

export default Upgrade;